import React, { useEffect, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, TrashIcon, DownloadIcon, XIcon, CheckIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, PlusIcon, TicketIcon, SearchIcon } from 'lucide-react';
import { Campaign } from '../../../shared/types/Campaign';
import { TimeSelector } from '../../analytics/components/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  BILASHOP2025: {
    id: 'BILASHOP2025',
    title: '[BILA Mart] 7-days Promotion for Shop',
    description: '[BILA Mart] 7-days Promotion for Shop',
    expiryDate: '07/03/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILASHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    transactionVolume: 47250,
    transactionCount: 63
  },
  BILA10MVR: {
    id: 'BILA10MVR',
    title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/01/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILA10MVR',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active',
    transactionVolume: 1284500,
    transactionCount: 1096
  },
  BULK2025: {
    id: 'BULK2025',
    title: 'Summer Festival Bulk Codes',
    description: 'Unique codes for summer festival attendees',
    startDate: '01/11/2025',
    expiryDate: '31/01/2026',
    discountPercentage: 15,
    fixedDiscountAmount: 0,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 50000,
    budgetUsed: 12500,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'PromptPay'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: '',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Bulk',
    minTransactionValue: 500,
    maxTransactionValue: 999999,
    minDiscount: 0,
    maxDiscount: 1000,
    status: 'active',
    transactionVolume: 312750,
    transactionCount: 245
  }
};
// Mock Code Batches
const mockCodeBatches = [{
  id: 'BATCH-001',
  dateCreated: '15/12/2025',
  codesGenerated: 500,
  usageLimitPerCode: 1,
  codesUsed: 420,
  status: 'Active'
}, {
  id: 'BATCH-002',
  dateCreated: '20/12/2025',
  codesGenerated: 1000,
  usageLimitPerCode: 2,
  codesUsed: 150,
  status: 'Active'
}];
// Mock Individual Codes for a Batch
const mockIndividualCodes = [{
  code: 'BULK-A1B2C3D4',
  usedCount: 1,
  lastUsed: '25/12/2025 14:30',
  status: 'Fully Redeemed'
}, {
  code: 'BULK-E5F6G7H8',
  usedCount: 0,
  lastUsed: '-',
  status: 'Available'
}, {
  code: 'BULK-I9J0K1L2',
  usedCount: 1,
  lastUsed: '26/12/2025 09:15',
  status: 'Fully Redeemed'
}, {
  code: 'BULK-M3N4O5P6',
  usedCount: 0,
  lastUsed: '-',
  status: 'Available'
}, {
  code: 'BULK-Q7R8S9T0',
  usedCount: 1,
  lastUsed: '28/12/2025 18:45',
  status: 'Fully Redeemed'
}];
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  // Codes Tab State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(100);
  const [usageLimit, setUsageLimit] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-10693" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-10694" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-10695" />
  }];
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
      // Auto-select first batch for bulk campaigns
      if (mockCampaigns[campaignId].codeStrategy === 'Bulk' && mockCodeBatches.length > 0) {
        setSelectedBatch(mockCodeBatches[0].id);
      }
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    navigate('/campaigns');
  };
  const navigateToEdit = () => {
    if (campaign) {
      navigate(`/campaign-form?id=${campaign.id}`);
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  const handleGenerateCodes = () => {
    // Simulate generating codes
    console.log(`Generating ${generateCount} codes with limit ${usageLimit}`);
    setShowGenerateModal(false);
    // In a real app, this would refresh the batches list
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-10696">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const getCodeStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Available: 'bg-green-50 text-green-700 border border-green-100',
      'Fully Redeemed': 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    const style = styles[status] || styles['Available'];
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-10697">
        {status}
      </span>;
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-10698">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2231A]" data-id="element-10699"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-2xl mx-auto mt-12" data-id="element-10700">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10701">
          {t('pages.CampaignDetails.campaign_not_found')}
        </h2>
        <p className="text-gray-500 mb-6" data-id="element-10702">
          {t('pages.CampaignDetails.the_campaign_you_are_looking_for')}
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-10703">
          {t('pages.CampaignDetails.back_to_campaigns')}
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-10704">
            <div data-id="element-10705">
              <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-10706">
                {t('pages.renderAnalyticsContent.conversion_funnel')}
              </h3>
              <ConversionFunnelChart data-id="element-10707" />
            </div>
            <div data-id="element-10708">
              <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-10709">
                {t('pages.renderAnalyticsContent.conversion_rates')}
              </h3>
              <ConversionRatePieChart data-id="element-10710" />
            </div>
          </div>;
      case 'usage':
        return <div data-id="element-10711">
            <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-10712">
              {t('pages.renderAnalyticsContent.code_usage_over_time_last_30')}
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-10713" />
          </div>;
      case 'volume':
        return <div data-id="element-10714">
            <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-10715">
              {t('pages.renderAnalyticsContent.payment_volume_over_time_last_30')}
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-10716" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-10717">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-10718">
        <div className="flex items-center gap-4" data-id="element-10719">
          <button onClick={navigateBack} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-10720">
            <ArrowLeftIcon size={20} data-id="element-10721" />
          </button>
          <div data-id="element-10722">
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3" data-id="element-10723">
              {campaign.title}
              {getStatusBadge(campaign.status)}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10724">
              {t('pages.CampaignDetails.campaign_id')} {campaign.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-10725">
          <button onClick={() => setShowExportModal(true)} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-10726">
            <DownloadIcon size={16} className="mr-2" data-id="element-10727" />
            {t('pages.CampaignDetails.export_csv')}
          </button>
          <button onClick={navigateToEdit} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-10728">
            <EditIcon size={16} className="mr-2" data-id="element-10729" />
            {t('pages.CampaignDetails.edit')}
          </button>
          <button className="flex items-center px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors shadow-sm" data-id="element-10730">
            <TrashIcon size={16} className="mr-2" data-id="element-10731" />
            {t('pages.CampaignDetails.delete')}
          </button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-id="element-10732">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200" data-id="element-10733">
            <div className="flex justify-between items-center mb-6" data-id="element-10734">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10735">
                {t('pages.CampaignDetails.export_campaign_data')}
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-10736">
                <XIcon size={20} aria-hidden="true" data-id="element-10737" />
              </button>
            </div>
            <div className="mb-6" data-id="element-10738">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-10739">
                {t('pages.CampaignDetails.select_time_period_for_export')}
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-10740" />
            </div>
            <div className="mb-8 bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-10741">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-10742">
                {t('pages.CampaignDetails.the_export_will_include')}
              </p>
              <ul className="space-y-1.5 text-sm text-gray-600" data-id="element-10743">
                <li className="flex items-center gap-2" data-id="element-10744">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-10745" />
                  {t('pages.CampaignDetails.daily_code_usage_statistics')}
                </li>
                <li className="flex items-center gap-2" data-id="element-10746">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-10747" />
                  {t('pages.CampaignDetails.conversion_metrics')}
                </li>
                <li className="flex items-center gap-2" data-id="element-10748">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-10749" />
                  {t('pages.CampaignDetails.transaction_volumes_and_amounts')}
                </li>
                <li className="flex items-center gap-2" data-id="element-10750">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-10751" />
                  {t('pages.CampaignDetails.user_engagement_data')}
                </li>
              </ul>
            </div>
            <div className="flex justify-end gap-3" data-id="element-10752">
              <button onClick={() => setShowExportModal(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-10753">
                {t('pages.CampaignDetails.cancel')}
              </button>
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-10754">
                  <DownloadIcon size={16} className="mr-2" data-id="element-10755" />
                  {t('pages.CampaignDetails.export_data')}
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-[#E2231A] text-white text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed" data-id="element-10756">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-900/20 border-t-gray-900 mr-2" data-id="element-10757"></div>
                  {t('pages.CampaignDetails.exporting')}
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-[#FEE7E5] text-gray-900 text-sm font-medium rounded-lg flex items-center cursor-not-allowed" data-id="element-10758">
                  <CheckIcon size={16} className="mr-2" data-id="element-10759" />
                  {t('pages.CampaignDetails.export_complete')}
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-10760">
                  {t('pages.CampaignDetails.try_again')}
                </button>}
            </div>
          </div>
        </div>}

      {/* Generate Codes Modal */}
      {showGenerateModal && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-id="element-10761">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200" data-id="element-10762">
            <div className="flex justify-between items-center mb-6" data-id="element-10763">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10764">
                {t('pages.CampaignDetails.generate_code_batch')}
              </h3>
              <button onClick={() => setShowGenerateModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-10765">
                <XIcon size={20} aria-hidden="true" data-id="element-10766" />
              </button>
            </div>

            <div className="space-y-4 mb-8" data-id="element-10767">
              <div data-id="element-10768">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10769">
                  {t('pages.CampaignDetails.number_of_codes_to_generate')}
                </label>
                <input type="number" min="1" max="10000" value={generateCount} onChange={e => setGenerateCount(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-10770" />
              </div>
              <div data-id="element-10771">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10772">
                  {t('pages.CampaignDetails.usage_limit_per_code')}
                </label>
                <input type="number" min="1" value={usageLimit} onChange={e => setUsageLimit(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-10773" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-10774">
                  {t('pages.CampaignDetails.how_many_times_each_individual_code')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3" data-id="element-10775">
              <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-10776">
                {t('pages.CampaignDetails.cancel')}
              </button>
              <button onClick={handleGenerateCodes} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-10777">
                {t('pages.CampaignDetails.generate_codes')}
              </button>
            </div>
          </div>
        </div>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10778">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-10779">
          <div className="flex justify-between items-start mb-2" data-id="element-10780">
            <div className="text-sm font-medium text-gray-500" data-id="element-10781">
              {t('components.CampaignList.budget_usage')}
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-10782">
              {calculateBudgetUsage()}%
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4 overflow-hidden" data-id="element-10783">
            <div className="bg-[#E2231A] h-1.5 rounded-full transition-all" style={{
            width: `${calculateBudgetUsage()}%`
          }} data-id="element-10784"></div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-10785">
            {campaign.budgetUsed.toLocaleString('en-US')}
            <span className="text-sm font-medium text-gray-500 ml-1" data-id="element-10786">
              / {campaign.budget.toLocaleString('en-US')} {campaign.currency}
            </span>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-10787">
          <div className="text-sm font-medium text-gray-500" data-id="element-10788">
            {t('pages.CampaignDetails.transaction_volume')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-10789">
            {campaign.currency}{' '}
            {campaign.transactionVolume?.toLocaleString('en-US') || '0.00'}
          </div>
          <div className="mt-1 text-sm text-gray-500" data-id="element-10790">
            {t('pages.CampaignDetails.from')} {campaign.transactionCount || 0}{' '}
            {t('pages.CampaignDetails.transactions')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-10791">
          <div className="text-sm font-medium text-gray-500" data-id="element-10792">
            {t('pages.CampaignDetails.volume_roi')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-10793">
            {campaign.budget > 0 ? `${((campaign.transactionVolume || 0) / campaign.budget).toFixed(1)}x` : '0x'}
          </div>
          <div className="mt-1 text-sm text-gray-500" data-id="element-10794">
            {t('pages.CampaignDetails.against_budget_of')} {campaign.currency}{' '}
            {campaign.budget.toLocaleString('en-US')}
          </div>
        </div>
      </div>

      {/* Main Content Card - Details */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-10795">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-10796">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-10797">
            {t('pages.CampaignDetails.campaign_details')}
          </h2>
        </div>
        <div className="p-6" data-id="element-10798">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8" data-id="element-10799">
            <div data-id="element-10800">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10801">
                {t('pages.CampaignDetails.category')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10802">{campaign.category}</div>
            </div>
            <div data-id="element-10803">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10804">
                {t('components.CampaignList.code_strategy')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10805">
                {campaign.codeStrategy}
              </div>
            </div>
            <div data-id="element-10806">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10807">
                {t('pages.CampaignDetails.campaign_code')}
              </div>
              <div className="text-sm text-gray-900 font-medium" data-id="element-10808">
                {campaign.code || 'N/A'}
              </div>
            </div>
            <div data-id="element-10809">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10810">
                {t('components.CampaignList.expiry_date')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10811">{campaign.expiryDate}</div>
            </div>
            <div data-id="element-10812">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10813">
                {t('components.CampaignList.discount')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10814">
                {campaign.discountPercentage > 0 ? `${campaign.discountPercentage}%` : `${campaign.fixedDiscountAmount} ${campaign.currency}`}
              </div>
            </div>
            <div data-id="element-10815">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10816">
                {t('pages.CampaignDetails.discount_carried_by')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10817">
                {campaign.discountCarriedBy}
                {campaign.merchantPercentage ? ` (${campaign.merchantPercentage}% Merchant)` : ''}
              </div>
            </div>
            <div className="md:col-span-3" data-id="element-10818">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10819">
                {t('pages.CampaignDetails.description')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10820">
                {campaign.description || 'No description provided.'}
              </div>
            </div>
          </div>
        </div>

        {/* Constraints & Targeting Section */}
        <div className="p-6 border-t border-gray-100" data-id="element-10821">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-10822">
            {t('pages.CampaignDetails.constraints_targeting')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8" data-id="element-10823">
            <div data-id="element-10824">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10825">
                {t('pages.CampaignDetails.start_date')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10826">
                {campaign.startDate || 'Not set'}
              </div>
            </div>
            <div data-id="element-10827">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10828">
                {t('pages.CampaignDetails.transaction_value_limits')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10829">
                {campaign.currency}{' '}
                {campaign.minTransactionValue.toLocaleString()} –{' '}
                {campaign.currency}{' '}
                {campaign.maxTransactionValue.toLocaleString()}
              </div>
            </div>
            <div data-id="element-10830">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10831">
                {t('pages.CampaignDetails.discount_limits')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10832">
                {campaign.currency} {campaign.minDiscount.toLocaleString()} –{' '}
                {campaign.currency} {campaign.maxDiscount.toLocaleString()}
              </div>
            </div>
            <div data-id="element-10833">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10834">
                {t('pages.CampaignDetails.valid_days')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10835">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </div>
            </div>
            <div data-id="element-10836">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10837">
                {t('pages.CampaignDetails.payment_methods')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10838">
                {campaign.paymentMethods && campaign.paymentMethods.length > 0 ? campaign.paymentMethods.join(', ') : 'All payment methods'}
              </div>
            </div>
            <div data-id="element-10839">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10840">
                {t('pages.CampaignDetails.budget_enforcement')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10841">
                {campaign.strictlyEnforceBudget ? 'Strictly enforced' : 'Soft limit'}
              </div>
            </div>
            <div data-id="element-10842">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10843">
                {t('pages.CampaignDetails.budget_type')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10844">
                {campaign.budgetType === 'recurring' ? `Recurring (${campaign.recurringBudgetPeriod || 'monthly'})` : 'Fixed'}
              </div>
            </div>
            <div data-id="element-10845">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10846">
                {t('pages.CampaignDetails.customer_usage_limits')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10847">
                {campaign.limitUsage ? `Limited to: ${campaign.limitUsage}` : 'No usage limits'}
              </div>
            </div>
            <div data-id="element-10848">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-10849">
                {t('pages.CampaignDetails.card_targeting')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-10850">
                {campaign.paymentMethods?.includes('Card') ? (() => {
                const rules: string[] = [];
                if (campaign.cardBrands && campaign.cardBrands.length > 0) rules.push(`Brands: ${campaign.cardBrands.join(', ')}`);
                if (campaign.cardType && campaign.cardType !== 'All Types') rules.push(`Type: ${campaign.cardType}`);
                if (campaign.cardBin) rules.push(`BINs: ${campaign.cardBin}`);
                if (campaign.cardIssuingCountry) rules.push(`Country: ${campaign.cardIssuingCountry}`);
                return rules.length > 0 ? rules.join(' • ') : 'All cards accepted';
              })() : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Analytics Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-10851">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-10852">
          <div className="flex items-center gap-2" data-id="element-10853">
            <PieChartIcon size={20} className="text-gray-400" data-id="element-10854" />
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-10855">
              {t('pages.CampaignDetails.analytics')}
            </h2>
          </div>

          {/* Analytics Tabs */}
          <div className="flex space-x-6 overflow-x-auto" role="tablist" aria-label="Campaign analytics tabs" data-id="element-10856">
            {analyticsTabs.map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls="campaign-analytics-tabpanel" id={`campaign-analytics-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-10857">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
        </div>

        <div className="p-6" data-id="element-10858">{renderAnalyticsContent()}</div>
      </div>

      {/* Code Batches Section - Standalone for Bulk campaigns */}
      {campaign.codeStrategy === 'Bulk' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-10859">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-10860">
            <div className="flex items-center gap-2" data-id="element-10861">
              <TicketIcon size={20} className="text-gray-400" data-id="element-10862" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-10863">
                {t('pages.CampaignDetails.code_batches')}
              </h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-10864">
                {mockCodeBatches.length} {t('pages.CampaignDetails.batches')}
              </span>
            </div>
            <button onClick={() => setShowGenerateModal(true)} className="flex items-center px-3 py-1.5 bg-[#E2231A] text-white text-sm font-medium rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm" data-id="element-10865">
              <PlusIcon size={16} className="mr-1.5" data-id="element-10866" />
              {t('pages.CampaignDetails.generate_batch')}
            </button>
          </div>

          {/* Batch Summary Cards */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/30" data-id="element-10867">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-10868">
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-10869">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-10870">
                  {t('pages.CampaignDetails.total_codes')}
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-10871">
                  {mockCodeBatches.reduce((sum, b) => sum + b.codesGenerated, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-10872">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-10873">
                  {t('pages.CampaignDetails.total_redeemed')}
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-10874">
                  {mockCodeBatches.reduce((sum, b) => sum + b.codesUsed, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-10875">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-10876">
                  {t('pages.CampaignDetails.redemption_rate')}
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-10877">
                  {Math.round(mockCodeBatches.reduce((sum, b) => sum + b.codesUsed, 0) / mockCodeBatches.reduce((sum, b) => sum + b.codesGenerated, 0) * 100)}
                  %
                </div>
              </div>
            </div>
          </div>

          {/* Batches Table */}
          <div className="overflow-x-auto" data-id="element-10878">
            <table className="w-full text-left border-collapse" data-id="element-10879">
              <thead data-id="element-10880">
                <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-10881">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10882">
                    {t('pages.CampaignDetails.batch_id')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10883">
                    {t('pages.CampaignDetails.date_created')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10884">
                    {t('pages.CampaignDetails.codes_generated')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10885">
                    {t('pages.CampaignDetails.usage_limit')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10886">
                    {t('pages.CampaignDetails.redemption')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10887">
                    {t('components.CampaignList.status')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10888">
                    {t('pages.CampaignDetails.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-10889">
                {mockCodeBatches.map(batch => <tr key={batch.id} onClick={() => setSelectedBatch(selectedBatch === batch.id ? null : batch.id)} className={`cursor-pointer transition-colors ${selectedBatch === batch.id ? 'bg-[#FEE7E5]/50' : 'hover:bg-gray-50'}`} data-id="element-10890">
                    <td className="px-6 py-4" data-id="element-10891">
                      <span className="text-sm font-medium text-gray-900 font-mono" data-id="element-10892">
                        {batch.id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-10893">
                      {batch.dateCreated}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium" data-id="element-10894">
                      {batch.codesGenerated.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-10895">
                      {batch.usageLimitPerCode}
                      {t('pages.CampaignDetails.per_code')}
                    </td>
                    <td className="px-6 py-4" data-id="element-10896">
                      <div className="flex items-center gap-3" data-id="element-10897">
                        <div className="w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden" data-id="element-10898">
                          <div className="bg-blue-500 h-1.5 rounded-full" style={{
                      width: `${batch.codesUsed / batch.codesGenerated * 100}%`
                    }} data-id="element-10899" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium" data-id="element-10900">
                          {batch.codesUsed}/{batch.codesGenerated}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-10901">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-10902">
                        {batch.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-10903">
                      <button className="text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-10904">
                        <DownloadIcon size={14} data-id="element-10905" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Expanded Individual Codes for selected batch */}
          {selectedBatch && <div className="border-t border-gray-200" data-id="element-10906">
              <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-10907">
                <div data-id="element-10908">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-10909">
                    {t('pages.CampaignDetails.individual_codes')}{' '}
                    {selectedBatch}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-10910">
                    {mockIndividualCodes.length}{' '}
                    {t('pages.CampaignDetails.codes_shown')}{' '}
                    {mockIndividualCodes.filter(c => c.status === 'Available').length}{' '}
                    {t('pages.CampaignDetails.available')}{' '}
                    {mockIndividualCodes.filter(c => c.status === 'Fully Redeemed').length}{' '}
                    {t('pages.CampaignDetails.redeemed')}
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-10911">
                  <div className="relative" data-id="element-10912">
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-10913" />

                    <input type="search" aria-label={t('pages.CampaignDetails.attrs.search_code')} placeholder={t('pages.CampaignDetails.attrs.search_code')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] bg-white" data-id="element-10914" />
                  </div>
                  <button className="flex items-center px-3 py-1.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-white transition-colors bg-white" data-id="element-10915">
                    <DownloadIcon size={14} className="mr-1.5" data-id="element-10916" />
                    {t('pages.CampaignDetails.export_codes')}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-10917">
                <table className="w-full text-left border-collapse" data-id="element-10918">
                  <thead className="bg-gray-50/50" data-id="element-10919">
                    <tr data-id="element-10920">
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10921">
                        {t('pages.CampaignDetails.code')}
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10922">
                        {t('pages.CampaignDetails.times_used')}
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10923">
                        {t('pages.CampaignDetails.last_used')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10924">
                        {t('components.CampaignList.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-10925">
                    {mockIndividualCodes.filter(code => !searchTerm || code.code.toLowerCase().includes(searchTerm.toLowerCase())).map((code, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-10926">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 font-mono" data-id="element-10927">
                            {code.code}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500" data-id="element-10928">
                            {code.usedCount}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500" data-id="element-10929">
                            {code.lastUsed}
                          </td>
                          <td className="px-6 py-3 text-right" data-id="element-10930">
                            {getCodeStatusBadge(code.status)}
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>}
    </div>;
}